import React, { useState, useEffect } from 'react';
import { getLegacyApiUrl } from '../utils/apiUtils';
import { useAuth } from '../contexts/AuthContext';
import LecturerSidebar from '../components/LecturerSidebar';

const LecturerProfile = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser && currentUser.role === 'lecturer') {
      setUserData(currentUser);
      setLoading(false);
    } else {
      // Fallback: Fetch from API if not in context
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${getLegacyApiUrl()}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          setUserData(data.user);
        } catch (error) {
          console.error('Error fetching profile:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchUserProfile();
    }
  }, [currentUser]);

  if (loading) {
    return (
      <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
        <LecturerSidebar />
        <div style={{marginLeft: 290, padding: '40px', flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <div style={{fontSize: 18, color: '#798AA3', fontFamily: 'Outfit, sans-serif'}}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
      <LecturerSidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh', background: '#F5F6FA', position: 'relative'}}>
        {/* Page Title */}
        <div style={{padding: '32px 40px 20px 40px'}}>
          <h1 style={{fontSize: 36, fontFamily: 'Outfit, sans-serif', fontWeight: '700', color: '#131A1A', margin: 0}}>
            Profile
          </h1>
          <p style={{fontSize: 16, color: '#798AA3', margin: '8px 0 0 0', fontFamily: 'Outfit, sans-serif'}}>
            View your personal information and account details
          </p>
        </div>

        <div style={{width: 800, margin: '0 auto', padding: '0 40px'}}>
          <div style={{position: 'relative', marginBottom: '80px'}}>
            {/* Cover Image */}
            <div style={{width: '100%', height: 160}}>
              <img 
                src="/assets/Content.png" 
                alt="Cover" 
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 16,
                  border: '1px solid #A8B9CA',
                  objectFit: 'cover'
                }}
              />
            </div>
            
            {/* Profile Image */}
            <div style={{
              position: 'absolute',
              bottom: -48,
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              <div 
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  border: '3px solid #FBFCFE',
                  background: '#F04915',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 36,
                  fontWeight: '700',
                  color: 'white',
                  fontFamily: 'Outfit, sans-serif'
                }}
              >
                {userData?.name ? userData.name.charAt(0).toUpperCase() : 'L'}
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div style={{textAlign: 'center', marginBottom: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit, sans-serif', fontWeight: '600', marginBottom: '8px'}}>
              {userData?.name || 'Lecturer Name'}
            </h2>
            <div style={{color: '#798AA3', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '400', marginBottom: '4px'}}>
              @{userData?.email?.split('@')[0] || 'username'}
            </div>
            <div style={{color: '#131A1A', fontSize: 18, fontFamily: 'Outfit, sans-serif', fontWeight: '500', textTransform: 'capitalize'}}>
              {userData?.role || 'Lecturer'}
            </div>
          </div>

          {/* ID Section */}
          <div style={{textAlign: 'center', marginBottom: '40px'}}>
            <div style={{fontSize: 28, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: '#131A1A'}}>
              {userData?.employee_id || 'EMP001'}
            </div>
            <div style={{color: '#798AA3', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '400'}}>
              Employee ID
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Account Information
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                <InfoBlock label="Full Name" value={userData?.name || 'N/A'} />
                <InfoBlock label="Employee ID" value={userData?.employee_id || 'N/A'} />
                <InfoBlock label="Email Address" value={userData?.email || 'N/A'} />
                <InfoBlock label="Contact Number" value={userData?.contact_no || 'N/A'} />
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div style={{marginBottom: '40px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Professional Information
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                <InfoBlock label="Department" value={userData?.department || 'N/A'} />
                <InfoBlock label="Position" value={userData?.position || 'Lecturer'} />
                <InfoBlock label="Date Joined" value={userData?.date_joined ? new Date(userData.date_joined).toLocaleDateString() : 'N/A'} />
                <InfoBlock label="Status" value={userData?.status || 'Active'} />
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          {userData?.address && (
            <div style={{marginBottom: '40px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
              <div style={{background: '#F04915', padding: '16px 24px'}}>
                <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                  Contact Information
                </h3>
              </div>
              <div style={{padding: '24px'}}>
                <InfoBlock label="Address" value={userData.address} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper component for info blocks
const InfoBlock = ({ label, value }) => (
  <div style={{marginBottom: '16px'}}>
    <div style={{color: '#798AA3', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
      {label}
    </div>
    <div style={{color: '#131A1A', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '500', wordBreak: 'break-word'}}>
      {value || 'N/A'}
    </div>
  </div>
);

export default LecturerProfile;
